import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogDescription 
} from '@/components/ui/dialog';
import { 
  RefreshCw, 
  Wifi, 
  WifiOff, 
  Monitor, 
  Smartphone, 
  User, 
  Building2, 
  Hotel, 
  Clock,
  Globe,
  AlertTriangle,
  Search,
  XCircle
} from 'lucide-react';

// Role badge colors
const ROLE_COLORS = {
  super_admin: 'bg-purple-500',
  company_admin: 'bg-blue-500',
  finance_admin: 'bg-indigo-500',
  hotel_admin: 'bg-green-500',
  manager: 'bg-teal-500',
  front_office: 'bg-orange-500',
  service_manager: 'bg-amber-500',
  restaurant_staff: 'bg-pink-500',
  housekeeping: 'bg-cyan-500',
  service_staff: 'bg-lime-500'
};

// Device icon based on device_info
const getDeviceIcon = (deviceInfo) => {
  if (!deviceInfo) return Monitor;
  const info = deviceInfo.toLowerCase();
  if (info.includes('mobile') || info.includes('android') || info.includes('iphone')) {
    return Smartphone;
  }
  return Monitor;
};

// Format date for display
const formatDateTime = (dateStr) => {
  if (!dateStr) return 'Unknown';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateStr;
  }
};

// Calculate session duration
const getSessionDuration = (startTime) => {
  if (!startTime) return 'Unknown';
  try {
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
    return `${diffMins}m`;
  } catch {
    return 'Unknown';
  }
};

export default function LiveSessions({ user: userProp }) {
  const [user] = useState(() => userProp || JSON.parse(localStorage.getItem('user') || '{}'));
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Kill session dialog
  const [killDialogOpen, setKillDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [killing, setKilling] = useState(false);
  
  // Kill all dialog
  const [killAllDialogOpen, setKillAllDialogOpen] = useState(false);
  const [killingAll, setKillingAll] = useState(false);

  const loadSessions = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await api.get('/auth/live-sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error(getErrorMessage(error, 'Failed to load live sessions'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => loadSessions(), 30000);
    return () => clearInterval(interval);
  }, [loadSessions]);

  const handleKillSession = async () => {
    if (!selectedSession) return;
    setKilling(true);
    try {
      await api.post(`/auth/kill-session/${selectedSession.user_id}`);
      toast.success(`Session terminated for ${selectedSession.user_email}`);
      setKillDialogOpen(false);
      setSelectedSession(null);
      loadSessions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to terminate session'));
    } finally {
      setKilling(false);
    }
  };

  const handleKillAllSessions = async () => {
    setKillingAll(true);
    try {
      const response = await api.post('/auth/kill-all-sessions');
      toast.success(response.data.message);
      setKillAllDialogOpen(false);
      loadSessions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to terminate sessions'));
    } finally {
      setKillingAll(false);
    }
  };

  const openKillDialog = (session) => {
    // Don't allow killing own session
    if (session.user_id === user.id) {
      toast.error('Cannot kill your own session. Use logout instead.');
      return;
    }
    setSelectedSession(session);
    setKillDialogOpen(true);
  };

  // Filter sessions based on search
  const filteredSessions = sessions.filter(session => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      session.user_name?.toLowerCase().includes(query) ||
      session.user_email?.toLowerCase().includes(query) ||
      session.user_role?.toLowerCase().includes(query) ||
      session.hotel_name?.toLowerCase().includes(query) ||
      session.company_name?.toLowerCase().includes(query) ||
      session.device_info?.toLowerCase().includes(query)
    );
  });

  // Get role title based on current user's role
  const getRoleTitle = () => {
    if (user.role === 'super_admin') return 'All Users (System-wide)';
    if (user.role === 'company_admin') return 'Company Users';
    if (user.role === 'hotel_admin') return 'Hotel Users';
    return 'Users';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-sessions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Live Sessions
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor and manage active user sessions - {getRoleTitle()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => loadSessions(true)}
            disabled={refreshing}
            data-testid="refresh-sessions-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {sessions.length > 1 && (
            <Button 
              variant="destructive"
              onClick={() => setKillAllDialogOpen(true)}
              data-testid="kill-all-btn"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Kill All Sessions
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Wifi className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Sessions</p>
              <p className="text-2xl font-bold">{sessions.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Unique Users</p>
              <p className="text-2xl font-bold">{new Set(sessions.map(s => s.user_id)).size}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <Hotel className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Hotels Active</p>
              <p className="text-2xl font-bold">{new Set(sessions.map(s => s.hotel_id).filter(Boolean)).size}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Active Sessions</CardTitle>
              <CardDescription>Users currently logged into the system</CardDescription>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users, hotels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredSessions.length === 0 ? (
            <div className="text-center py-12">
              <WifiOff className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? 'No sessions match your search' : 'No active sessions'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    {user.role === 'super_admin' && <TableHead>Company</TableHead>}
                    {(user.role === 'super_admin' || user.role === 'company_admin') && <TableHead>Hotel</TableHead>}
                    <TableHead>Device</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Session Start</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSessions.map((session) => {
                    const DeviceIcon = getDeviceIcon(session.device_info);
                    const isCurrentUser = session.user_id === user.id;
                    
                    return (
                      <TableRow key={session.user_id} className={isCurrentUser ? 'bg-primary/5' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">
                                {session.user_name}
                                {isCurrentUser && (
                                  <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground">{session.user_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${ROLE_COLORS[session.user_role] || 'bg-gray-500'} text-white capitalize`}>
                            {session.user_role?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        {user.role === 'super_admin' && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">{session.company_name || 'N/A'}</span>
                            </div>
                          </TableCell>
                        )}
                        {(user.role === 'super_admin' || user.role === 'company_admin') && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Hotel className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">{session.hotel_name || 'N/A'}</span>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <DeviceIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{session.device_info || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Globe className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-mono">{session.ip_address || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{formatDateTime(session.session_started)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {getSessionDuration(session.session_started)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openKillDialog(session)}
                            disabled={isCurrentUser}
                            className={isCurrentUser ? 'opacity-50' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}
                            data-testid={`kill-session-${session.user_id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Kill
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kill Single Session Dialog */}
      <Dialog open={killDialogOpen} onOpenChange={setKillDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Terminate Session
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to terminate this user's session? They will be logged out immediately.
            </DialogDescription>
          </DialogHeader>
          {selectedSession && (
            <div className="py-4 space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <strong>User:</strong> {selectedSession.user_name} ({selectedSession.user_email})
                </p>
                <p className="text-sm">
                  <strong>Role:</strong> {selectedSession.user_role?.replace('_', ' ')}
                </p>
                <p className="text-sm">
                  <strong>Device:</strong> {selectedSession.device_info || 'Unknown'}
                </p>
                <p className="text-sm">
                  <strong>Session Started:</strong> {formatDateTime(selectedSession.session_started)}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setKillDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleKillSession}
              disabled={killing}
            >
              {killing ? 'Terminating...' : 'Terminate Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kill All Sessions Dialog */}
      <Dialog open={killAllDialogOpen} onOpenChange={setKillAllDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Terminate All Sessions
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to terminate ALL active sessions? All users (except you) will be logged out immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will log out {sessions.filter(s => s.user_id !== user.id).length} user(s) immediately.
                This action cannot be undone.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKillAllDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleKillAllSessions}
              disabled={killingAll}
            >
              {killingAll ? 'Terminating...' : 'Terminate All Sessions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
